import { HolderOutlined } from '@ant-design/icons';
import { ProductDoc } from '@gooduncles/gu-app-schema';
import type { XYCoord } from 'dnd-core';
import { FC, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import FavoriteProductRow from './FavoriteProductRow';
import classes from './FavoriteProductRow.module.scss';

export const FAVORITE_DND_TYPE = 'FAVORITE_PRODUCT_ROW';

interface DragItem {
  index: number;
  productId: string;
}

interface DraggableFavoriteItemProps {
  index: number;
  productId: string;
  product: ProductDoc | null;
  moveItem: (dragIndex: number, hoverIndex: number) => void;
  onRemove: () => void;
}

const DraggableFavoriteItem: FC<DraggableFavoriteItemProps> = ({ index, productId, product, moveItem, onRemove }) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, preview] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: FAVORITE_DND_TYPE,
    item: { index, productId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop<DragItem, void>({
    accept: FAVORITE_DND_TYPE,
    hover: (dragItem, monitor) => {
      if (!ref.current) return;
      const dragIndex = dragItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveItem(dragIndex, hoverIndex);
      dragItem.index = hoverIndex;
    },
  });

  preview(drop(ref));

  return (
    <FavoriteProductRow
      ref={ref}
      productId={productId}
      product={product}
      onRemove={onRemove}
      isDragging={isDragging}
      dragHandle={
        <div ref={drag} className={classes.dragHandle}>
          <HolderOutlined />
        </div>
      }
    />
  );
};

export default DraggableFavoriteItem;
